import React, { useState, useEffect, createContext } from 'react'
import { Appearance, Platform, View } from 'react-native'
import { Theme } from '../../style/theme-types'
import {
  dark as darkTheme,
  light as lightTheme,
  climateFirst as climateFirstTheme
} from '../../style/theme'
import { Storage } from '../pwa'

type ThemeContextType = {
  theme: Theme
  setTheme: (theme: Theme | string) => void
}

export const ThemeContext = createContext<ThemeContextType>({
  theme: lightTheme,
  setTheme: (nextTheme: Theme | string) => {
    // default no-op-ish implementation: normalize to light for SSR/initial render
    if (typeof nextTheme !== 'string') {
      return
    }
  }
})

type ThemeProviderProps = {
  children: React.ReactNode
  /**
   * Forces the provider to a specific theme and disables Storage/Appearance
   * loading. Used for embed contexts where the host page picks the theme via
   * a URL parameter and the iframe must not fall back to stored or system
   * preferences.
   */
  overrideTheme?: string | null
}

const resolveInitialTheme = (overrideTheme?: string | null): Theme => {
  if (!overrideTheme) {
    return lightTheme
  }
  if (overrideTheme === 'dark') {
    return darkTheme
  }
  if (overrideTheme === 'climateFirst') {
    return climateFirstTheme
  }
  return lightTheme
}

export const ThemeProvider = ({ children, overrideTheme }: ThemeProviderProps): JSX.Element => {
  const [theme, setTheme] = useState<Theme>(() => resolveInitialTheme(overrideTheme))

  useEffect(() => {
    if (overrideTheme) {
      // Lock to the override; do not read from Storage or system Appearance.
      setThemeContext(overrideTheme)
      return
    }

    const loadTheme = async (): Promise<void> => {
      const storedTheme = await Storage.getItem('ecountabl-theme')
      const definedTheme = storedTheme && storedTheme !== 'system'
      if (definedTheme) {
        setThemeContext(storedTheme as string)
      } else {
        const colorScheme = Appearance.getColorScheme()
        setThemeContext(colorScheme as 'light' | 'dark' | null)
      }
    }

    void loadTheme()

    const subscription = Appearance.addChangeListener(async ({ colorScheme }: { colorScheme: 'light' | 'dark' | null }) => {
      const storedTheme = await Storage.getItem('ecountabl-theme')
      const followDeviceTheme = !storedTheme || storedTheme === 'system'
      if (followDeviceTheme) {
        setThemeContext(colorScheme)
      }
    })

    return () => {
      subscription.remove()
    }
  }, [overrideTheme])

  useEffect(() => {
    // if theme changes, assign a style to web page for scrollbar styling
    if (Platform.OS === 'web') {
      // eslint-disable-next-line no-undef
      const style = document.createElement('style')
      // Use textContent (not innerHTML) so the value is treated as a CSS text
      // node and never enters the HTML parser. scrollbarThumb comes from the
      // built-in Theme object, but textContent removes the innerHTML sink
      // entirely as defense-in-depth.
      style.textContent = `
        ::-webkit-scrollbar {
          width: 10px; /* vertical scrollbar width */
          height: 10px; /* horizontal scrollbar height */
          background-color: transparent;
        }
        ::-webkit-scrollbar-thumb {
          background-color: ${theme.colors.scrollbarThumb}D0;
          border-radius: 10px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background-color: ${theme.colors.scrollbarThumb}80;
          cursor: grab;
        }
        ::-webkit-scrollbar-thumb:active {
          background-color: ${theme.colors.scrollbarThumb}40;
          cursor: grabbing;
        }
      `
      // eslint-disable-next-line no-undef
      document.head.appendChild(style)
    }
  }, [theme])

  const setThemeContext = (nextTheme: Theme | string): void => {
    if (typeof nextTheme !== 'string') {
      setTheme(nextTheme)
      return
    }

    if (nextTheme === 'dark') {
      setTheme(darkTheme)
      return
    }
    if (nextTheme === 'climateFirst') {
      setTheme(climateFirstTheme)
      return
    }
    // default and 'light'
    setTheme(lightTheme)
  }

  return (
    <ThemeContext.Provider value={{ theme, setTheme: setThemeContext }}>
      <View style={{flex: 1, backgroundColor: theme.colors.bgMain}}>
        {children}
      </View>
    </ThemeContext.Provider>
  )
}